import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import api from "../lib/api";

const STORAGE_KEY = "ak_currency_override_v2";
const SESSION_DETECTED_KEY = "ak_currency_detected_v2";

const DEFAULT_STATE = {
  currency: "INR",
  symbol: "₹",
  rate: 1.0,           // multiply an INR amount by this to display
  usd_to_inr: 1.0,     // for the disclaimer footer
  country: "IN",
  decimal_places: 0,
  loading: true,
  manuallyChosen: false,
};

const CurrencyContext = createContext(null);

/**
 * Format an INR-denominated amount into the currently-active currency
 * string. We charge in INR via Razorpay regardless; this is DISPLAY-ONLY.
 *   formatPrice(40)        → "$0.42"   (US visitor)  OR  "₹40" (IN visitor)
 *   formatPrice(0)         → "Free"    (special-case: cleaner than "$0.00")
 *   formatPrice(2999)      → "$31.34"  OR  "₹2,999"
 */
function formatPriceWith(state, inrAmount, { withSymbol = true, free = "Free" } = {}) {
  const n = Number(inrAmount);
  if (!isFinite(n)) return withSymbol ? `${state.symbol}—` : "—";
  if (n === 0) return free;
  const display = n * state.rate;
  const decimals = state.decimal_places ?? (state.currency === "INR" ? 0 : 2);
  // For INR we use the en-IN locale to get "1,23,456" lakh-style grouping.
  // For everything else, en-US.
  const locale = state.currency === "INR" ? "en-IN" : "en-US";
  // Below $1 we show 2 decimals so "$0.42/min" doesn't round to "$0".
  // Above $1 we still use the configured decimals for consistency.
  const effectiveDecimals = state.currency === "INR" ? decimals : (display < 1 ? 2 : decimals);
  const formatted = display.toLocaleString(locale, {
    minimumFractionDigits: effectiveDecimals,
    maximumFractionDigits: effectiveDecimals,
  });
  return withSymbol ? `${state.symbol}${formatted}` : formatted;
}

export function CurrencyProvider({ children }) {
  const [state, setState] = useState(() => {
    // One-time migration: clear the stale v1 cache from before the
    // "default to INR" fix. Without this, returning users would still see
    // their incorrectly-cached USD price.
    try {
      localStorage.removeItem("ak_currency_override_v1");
      sessionStorage.removeItem("ak_currency_detected_v1");
    } catch { /* noop */ }
    // Optimistic restore from session — avoids the price flash on every page load.
    try {
      const cached = sessionStorage.getItem(SESSION_DETECTED_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        return { ...DEFAULT_STATE, ...parsed, loading: false };
      }
    } catch { /* noop */ }
    return DEFAULT_STATE;
  });

  // Resolve auto-detect (server-side via CF-IPCountry) once per session.
  useEffect(() => {
    let cancelled = false;
    const stored = (() => {
      try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
    })();
    const params = stored ? { country: stored } : {};
    api.get("/geo/currency", { params })
      .then(({ data }) => {
        if (cancelled) return;
        const next = {
          currency: data.currency,
          symbol: data.symbol,
          rate: Number(data.rate) || 1,
          usd_to_inr: Number(data.usd_to_inr) || 1,
          country: data.country,
          decimal_places: data.decimal_places,
          loading: false,
          manuallyChosen: !!stored,
        };
        setState(next);
        try {
          sessionStorage.setItem(SESSION_DETECTED_KEY, JSON.stringify(next));
        } catch { /* noop */ }
      })
      .catch(() => {
        if (!cancelled) setState((s) => ({ ...s, loading: false }));
      });
    return () => { cancelled = true; };
  }, []);

  const setCurrency = useCallback((nextCurrency) => {
    // Manual override — store the country code so the next geo/currency
    // call returns the right shape; "IN" → INR, "US" → USD.
    const countryCode = nextCurrency === "INR" ? "IN" : "US";
    try { localStorage.setItem(STORAGE_KEY, countryCode); } catch { /* noop */ }
    api.get("/geo/currency", { params: { country: countryCode } })
      .then(({ data }) => {
        const next = {
          currency: data.currency,
          symbol: data.symbol,
          rate: Number(data.rate) || 1,
          usd_to_inr: Number(data.usd_to_inr) || 1,
          country: data.country,
          decimal_places: data.decimal_places,
          loading: false,
          manuallyChosen: true,
        };
        setState(next);
        try { sessionStorage.setItem(SESSION_DETECTED_KEY, JSON.stringify(next)); } catch { /* noop */ }
      })
      .catch(() => { /* noop */ });
  }, []);

  const value = useMemo(() => ({
    ...state,
    setCurrency,
    formatPrice: (inrAmount, opts) => formatPriceWith(state, inrAmount, opts),
    // True iff we're showing USD prices — useful for places that need to
    // append an "(approx, charged in INR)" footnote.
    showingUsd: state.currency === "USD",
  }), [state, setCurrency]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency must be used inside <CurrencyProvider>");
  return ctx;
}
